/**
 * Row-grouping dimensions for Replenishment -> Inventory health, shared by the grid and
 * the brand-default picker in Settings.
 *
 * Deliberately NOT the same vocabulary as the Inventory grids' rollup keys
 * (`constants/inventoryRollup`). Those are row *property* names, camelCase, and open-ended
 * because entity attributes join them as `attr:<uuid>`. These are grouping *modes*: a
 * closed enum, and two of them are not product properties at all — SUPPLIER reads
 * `vendorName` because a PO is raised per supplier, and MATERIAL_TYPE only exists in the
 * Materials view. Translating between the two would have to fail on those, so they stay
 * separate and a brand sets each page's default independently.
 */

export type GroupBy =
    | 'NONE'
    | 'SUPPLIER'
    | 'CATEGORY'
    | 'PRODUCT_GROUP'
    | 'TIER'
    | 'MATERIAL_TYPE';

export const NO_GROUPING: GroupBy = 'NONE';

export type GroupingOptionDef = { value: GroupBy; label: string };

export const PRODUCT_GROUPINGS: GroupingOptionDef[] = [
    { value: 'NONE', label: 'No grouping' },
    { value: 'SUPPLIER', label: 'By supplier' },
    { value: 'CATEGORY', label: 'By category' },
    { value: 'PRODUCT_GROUP', label: 'By product group' },
    { value: 'TIER', label: 'By tier' },
];

export const MATERIAL_GROUPINGS: GroupingOptionDef[] = [
    { value: 'NONE', label: 'No grouping' },
    { value: 'SUPPLIER', label: 'By supplier' },
    { value: 'CATEGORY', label: 'By category' },
    { value: 'MATERIAL_TYPE', label: 'By material type' },
];

/**
 * The brand default is only ever applied to the page's opening state, and the page always
 * opens on Finished goods — so a stored default is validated against the Finished-goods
 * dimensions. MATERIAL_TYPE is therefore not offerable as a default, which is why Settings
 * shows this list rather than the union.
 */
const isProductGrouping = (value: string | null | undefined): value is GroupBy =>
    !!value && PRODUCT_GROUPINGS.some((option) => option.value === value);

export type ResolvedGrouping = { groupBy: GroupBy; subGroupBy: GroupBy };

/**
 * The grouping a brand's default resolves to, or the flat list when it names something
 * this page cannot group on.
 *
 * Enforces the same two invariants `GroupingLevels` does, so a default can never put the
 * control into a state a user could not have produced themselves:
 *   - no first level means no second level
 *   - the second level cannot repeat the first
 */
export const resolveOrgGrouping = (
    storedGroupBy: string | null | undefined,
    storedSubGroupBy: string | null | undefined,
): ResolvedGrouping => {
    if (!isProductGrouping(storedGroupBy) || storedGroupBy === NO_GROUPING) {
        return { groupBy: NO_GROUPING, subGroupBy: NO_GROUPING };
    }
    const sub =
        isProductGrouping(storedSubGroupBy) && storedSubGroupBy !== storedGroupBy
            ? storedSubGroupBy
            : NO_GROUPING;
    return { groupBy: storedGroupBy, subGroupBy: sub };
};
